// Using Amplitude for analytics with platform-specific implementations

import { Platform } from 'react-native'
import {
  Identify,
  PLATFORM,
  STORAGE_CONFIG,
  init,
  isLocalhost,
  setUserId,
  track,
} from './amplitude'

// Define event names as constants to prevent typos and enable autocomplete
export const AnalyticsEvents = {
  SCREEN_VIEW: 'screen_view',
  BUTTON_CLICK: 'button_click',
  TASK_CREATED: 'task_created',
  TASK_COMPLETED: 'task_completed',
  FOCUS_SESSION_STARTED: 'focus_session_started',
  FOCUS_SESSION_COMPLETED: 'focus_session_completed',
  MESSAGE_SENT: 'message_sent',
  TASK_UPDATED: 'task_updated',
  // Add more events as needed
} as const

// Type for event names
export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents]

// Interface for common event properties
export interface CommonEventProperties {
  platform: string
  app_version: string
  screen_name?: string
  // Add more common properties
}

const API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY || '8936cc0f90ca2dfaedde4960a0c8f2e0'
const IS_DEV = process.env.NODE_ENV === 'development'

class AnalyticsService {
  private initialized = false
  private pendingEvents: Array<{ name: AnalyticsEventName; properties?: Record<string, any> }> = []

  constructor() {
    this.initializeAmplitude()
  }

  private async initializeAmplitude() {
    try {
      // Completely skip Amplitude on localhost or in dev environment
      if (isLocalhost() || IS_DEV) {
        if (IS_DEV) {
          console.log('Skipping Amplitude initialization on localhost/dev')
        }
        this.initialized = true
        return
      }

      // Only initialize in production and not on localhost
      if (PLATFORM === 'web') {
        await init(API_KEY, {
          logLevel: 0, // Silent in production
          defaultTracking: true,
          ...STORAGE_CONFIG,
        })
      } else {
        await init(API_KEY)
      }

      this.initialized = true
      this.flushPendingEvents()
    } catch (error) {
      // Set initialized to true even if there's an error to prevent further attempts
      this.initialized = true
      if (IS_DEV) {
        console.warn('Amplitude initialization warning:', error)
      }
    }
  }

  private flushPendingEvents() {
    // Skip if on localhost/dev - just clear the queue
    if (isLocalhost() || IS_DEV) {
      this.pendingEvents = []
      return
    }

    this.pendingEvents.forEach(({ name, properties }) => {
      this.logEvent(name, properties)
    })
    this.pendingEvents = []
  }

  public logEvent(eventName: AnalyticsEventName, properties?: Record<string, any>) {
    if (!this.initialized) {
      this.pendingEvents.push({ name: eventName, properties })
      return
    }

    // Skip actually sending events on localhost/dev
    if (isLocalhost() || IS_DEV) {
      if (IS_DEV) {
        console.log(`[DEV ONLY] Analytics event (not sent): ${eventName}`, properties)
      }
      return
    }

    const enrichedProperties = {
      ...properties,
      platform: PLATFORM,
      timestamp: new Date().toISOString(),
    }

    try {
      track(eventName, enrichedProperties)
    } catch (error) {
      // Silent failure
    }
  }

  public setUserId(userId: string | null) {
    if (!this.initialized) return

    // Skip on localhost/dev
    if (isLocalhost() || IS_DEV) return

    try {
      setUserId(userId || undefined)
    } catch (error) {
      // Silent failure
    }
  }

  public setUserProperties(properties: Record<string, any>) {
    if (!this.initialized) return

    // Skip on localhost/dev
    if (isLocalhost() || IS_DEV) return

    try {
      const identify = Identify()
      Object.entries(properties).forEach(([key, value]) => {
        identify.set(key, value)
      })
    } catch (error) {
      // Silent failure
    }
  }
}

// Export a singleton instance
export const Analytics = new AnalyticsService()

// Usage example:
// Analytics.logEvent(AnalyticsEvents.TASK_CREATED, { taskId: '123', taskName: 'My Task' });
